import {
  APPROVED_PROMOTIONS_HEADER,
  EXPIRED_PROMOTIONS_HEADER,
  PROMOTIONS_PENDING_APPROVAL_HEADER
} from '@library/constants/promotions';
import { Table, TableBody, TableHeader } from '@library/components/table';
import { useCallback, useState } from 'react';
import {
  useGetApprovedPromotionsQuery,
  useGetExpiredPromotionsQuery,
  useGetPromotionsPendingApprovalQuery
} from '@bo-services/promotionsApi';
import { Loading } from '@library/components/Loading';
import { NoResults } from '@library/components/NoResults';
import { PaginationButtons } from '@library/components/buttons';
import type { PromotionApprovalsListType } from '@library/types/promotions';
import { PromotionApprovalsRow } from '@bo-components/promotions/approvals/PromotionApprovalsRow';

interface PromotionApprovalsListProps {
  listType: PromotionApprovalsListType;
}

const tableConfig = {
  pending: {
    query: useGetPromotionsPendingApprovalQuery,
    headers: PROMOTIONS_PENDING_APPROVAL_HEADER,
    typeDescription: 'Promotions Pending Approval'
  },
  approved: {
    query: useGetApprovedPromotionsQuery,
    headers: APPROVED_PROMOTIONS_HEADER,
    typeDescription: 'Approved Promotions'
  },
  expired: {
    query: useGetExpiredPromotionsQuery,
    headers: EXPIRED_PROMOTIONS_HEADER,
    typeDescription: 'Expired Promotions'
  }
};

export const PromotionApprovalsList = ({
  listType
}: PromotionApprovalsListProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(25);

  const { query, headers, typeDescription } = tableConfig[listType];
  const { data: promotions, isFetching } = query({
    page: currentPage,
    perPage
  });

  const loadingMessage = `Loading ${typeDescription}`;
  const emptyStateMessage = `No ${typeDescription}`;

  const handlePageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
    },
    [setCurrentPage]
  );

  if (isFetching) {
    return <Loading loadingMessage={loadingMessage} />;
  }

  if (!promotions?.data.length) {
    return <NoResults className="m-0" label={emptyStateMessage} />;
  }

  return (
    <Table>
      <div
        className="flex flex-col overflow-x-auto"
        data-testid="PromotionApprovals"
      >
        <TableHeader headers={headers} />
        <TableBody>
          {promotions.data.map((promotion) => (
            <PromotionApprovalsRow
              key={promotion.id}
              promotion={promotion}
              listType={listType}
            />
          ))}
        </TableBody>
      </div>

      <PaginationButtons
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        totalPages={promotions?.meta?.total_pages ?? 0}
      />
    </Table>
  );
};
